export function parseEuroToCents(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Math.round(value * 100);
  const normalized = String(value).trim().replace(/\s|€|EUR/gi, '');
  if (!normalized) return null;
  const sign = normalized.startsWith('-') ? -1 : 1;
  const unsigned = normalized.replace(/^[+-]/, '');
  if (!/^\d+(?:[.,]\d+)*$/.test(unsigned)) return null;
  const lastComma = unsigned.lastIndexOf(',');
  const lastDot = unsigned.lastIndexOf('.');
  const lastSeparator = Math.max(lastComma, lastDot);
  const digitsAfterSeparator = lastSeparator >= 0 ? unsigned.length - lastSeparator - 1 : 0;
  const separatorCount = (unsigned.match(/[.,]/g) || []).length;
  const isDecimal = lastSeparator >= 0 && (
    digitsAfterSeparator <= 2
    || (lastComma >= 0 && lastDot >= 0)
    || (separatorCount === 1 && digitsAfterSeparator !== 3)
  );
  const integerPart = (isDecimal ? unsigned.slice(0, lastSeparator) : unsigned).replace(/[.,]/g, '');
  const decimals = isDecimal ? unsigned.slice(lastSeparator + 1).padEnd(2, '0').slice(0, 2) : '00';
  const euros = Number(integerPart || '0');
  if (!Number.isSafeInteger(euros)) return null;
  return sign * (euros * 100 + Number(decimals));
}

export function formatCentsForInput(cents) {
  if (!Number.isInteger(cents)) return '';
  return (cents / 100).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function splitInstallments(totalCents, count) {
  const safeTotal = Math.max(0, Number.isInteger(totalCents) ? totalCents : 0);
  const safeCount = Math.max(1, Number.parseInt(count, 10) || 1);
  const base = Math.floor(safeTotal / safeCount);
  return Array.from({ length: safeCount }, (_, index) =>
    index === safeCount - 1 ? safeTotal - base * (safeCount - 1) : base
  );
}

export function rebalanceInstallments(amounts, changedIndex, nextCents, totalCents) {
  if (!Array.isArray(amounts) || amounts.length === 0) return { amounts: [], error: 'Piano rateale vuoto' };
  if (!Number.isInteger(nextCents)) return { amounts: [...amounts], error: '' };
  if (nextCents < 0) return { amounts: [...amounts], error: 'L’importo della rata non può essere negativo' };
  const adjustmentIndex = changedIndex === amounts.length - 1 && amounts.length > 1
    ? amounts.length - 2
    : amounts.length - 1;
  const fixedTotal = amounts.reduce((sum, amount, index) =>
    index === changedIndex || index === adjustmentIndex ? sum : sum + amount, 0);
  const residual = totalCents - fixedTotal - nextCents;
  if (residual < 0) {
    return { amounts: [...amounts], error: 'La rata supera il residuo disponibile' };
  }
  const next = [...amounts];
  next[changedIndex] = nextCents;
  next[adjustmentIndex] = residual;
  return { amounts: next, error: '' };
}

export function commitInstallmentEdit(currentAmounts, changedIndex, rawValue, totalCents) {
  const nextCents = parseEuroToCents(rawValue);
  if (String(rawValue ?? '').trim() === '') return { amounts: [], error: 'Inserisci l’importo della rata' };
  if (!Number.isInteger(nextCents)) return { amounts: [], error: 'Inserisci un importo valido con massimo due decimali' };
  if (nextCents < 0) return { amounts: [], error: 'L’importo della rata non può essere negativo' };
  if (nextCents > totalCents) return { amounts: [], error: 'La rata non può superare il valore dell’affare' };

  const amounts = Array.isArray(currentAmounts) ? [...currentAmounts] : splitInstallments(totalCents, currentAmounts);
  const safeCount = amounts.length;
  if (changedIndex < 0 || changedIndex >= safeCount) return { amounts: [], error: 'Rata non valida' };
  if (safeCount === 1) {
    return nextCents === totalCents
      ? { amounts: [nextCents], error: '' }
      : { amounts: [], error: 'La rata unica deve coincidere con il valore dell’affare' };
  }

  // Only the last row balances the total; previous manual amounts are never redistributed.
  if (changedIndex !== safeCount - 1) amounts[changedIndex] = nextCents;
  amounts[safeCount - 1] = totalCents - amounts.slice(0, -1).reduce((sum, amount) => sum + amount, 0);
  return {
    amounts,
    error: amounts[safeCount - 1] < 0 ? 'La somma delle rate supera il totale: riduci gli importi delle altre rate' : '',
  };
}

export function isValidMoneyDraft(value) {
  return /^\d*(?:[.,]\d{0,2})?$/.test(String(value ?? '').trim());
}

export function validateInstallmentAmounts(amounts, totalCents) {
  if (!Array.isArray(amounts) || amounts.length === 0) return 'Inserisci almeno una rata';
  if (amounts.some((amount) => !Number.isInteger(amount) || amount < 0)) return 'Gli importi delle rate non sono validi';
  return amounts.reduce((sum, amount) => sum + amount, 0) === totalCents
    ? ''
    : 'La somma delle rate deve coincidere con il valore dell’affare';
}
